import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheck, faChevronUp, faPlus, faTags, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useElementBounding, whenever } from "@vueuse/core";
import { computed, nextTick, ref, watch } from "vue";
// @ts-ignore missing types
import Vue2Teleport from "vue2-teleport";
import { useUid } from "@/composables/utils/uid";
import { normalizeTag } from "@/stores/userTagsStore";
export default /*#__PURE__*/ _defineComponent({
    __name: 'HeadlessMultiselect',
    props: {
        options: null,
        selected: null,
        maxShownOptions: { default: 50 },
        placeholder: { default: "type to search" },
        id: { default: () => useUid("headless-multiselect-").value },
        validator: { type: Function, default: () => () => true }
    },
    emits: ["input", "addOption", "selected"],
    setup(__props, { emit }) {
        const props = __props;
        /**
         * This component is only the select element of a multiselect.
         * It does not display selected options.
         * It is a building block for building a custom Multiselect,
         * not a fully featured Multiselect alternative
         */
        library.add(faCheck, faChevronUp, faPlus, faTags, faTimes);
        const inputField = ref(null);
        const openButton = ref(null);
        /** controls the open state of the select popup */
        const isOpen = ref(false);
        /** open popup and focus search field */
        async function open() {
            isOpen.value = true;
            await nextTick();
            inputField.value?.focus();
        }
        /** close popup and focus open button */
        async function close(refocus = true) {
            isOpen.value = false;
            if (refocus) {
                await nextTick();
                openButton.value?.focus();
            }
            searchValue.value = "";
        }
        /** the text in the input field */
        const searchValue = ref("");
        const trimmedSearchValue = computed(() => searchValue.value.trim());
        const optionsAsSet = computed(() => new Set(props.options));
        const searchValueEmpty = computed(() => trimmedSearchValue.value === "");
        const searchValueValid = computed(() => searchValueEmpty.value || props.validator(trimmedSearchValue.value));
        /** options which partially match the search value */
        const filteredOptions = computed(() => {
            if (searchValueEmpty.value) {
                return props.options;
            }
            else {
                return props.options.filter((option) => option.includes(trimmedSearchValue.value));
            }
        });
        /** options trimmed to `maxShownOptions` and reordered so the search value appears first */
        const trimmedOptions = computed(() => {
            const optionsSliced = filteredOptions.value
                .slice(0, props.maxShownOptions)
                .map((tag) => tag.replace(/^name:/, "#"));
            // remove search value to put it in front
            const optionsSet = new Set(optionsSliced);
            optionsSet.delete(trimmedSearchValue.value);
            const optionsTrimmed = Array.from(optionsSet);
            if (!searchValueEmpty.value) {
                optionsTrimmed.unshift(trimmedSearchValue.value);
            }
            return optionsTrimmed;
        });
        /** the option which will be added when the `Enter` key is pressed */
        const highlightedOption = ref(0);
        watch(() => trimmedSearchValue.value, () => {
            highlightedOption.value = 0;
        });
        function onOptionHover(index) {
            highlightedOption.value = index;
        }
        /** allows for selecting from the autocomplete list while focusing the input field */
        function onInputUp() {
            if (highlightedOption.value > 0) {
                highlightedOption.value -= 1;
            }
            getOptionWithId(highlightedOption.value)?.scrollIntoView({ block: "center" });
        }
        /** allows for selecting from the autocomplete list while focusing the input field */
        function onInputDown() {
            if (highlightedOption.value < trimmedOptions.value.length - 1) {
                highlightedOption.value += 1;
            }
            getOptionWithId(highlightedOption.value)?.scrollIntoView({ block: "center" });
        }
        /** finds the html element for the option with given id */
        function getOptionWithId(id) {
            return document.querySelector(`#${props.id}-option-${id}`);
        }
        /**
         * Emits an input event with the new selected options,
         * or emits an addOption event, if the selected option
         * was not part of the provided options
         */
        function onOptionSelected(option) {
            option = normalizeTag(option);
            if (!optionsAsSet.value.has(option)) {
                emit("addOption", option);
                return;
            }
            const set = new Set(props.selected);
            if (set.has(option)) {
                set.delete(option);
            }
            else {
                set.add(option);
                emit("selected", option);
            }
            emit("input", Array.from(set));
        }
        /** handles the Enter key on the input element */
        function onInputEnter() {
            const option = trimmedOptions.value[highlightedOption.value];
            if (option) {
                onOptionSelected(option);
                searchValue.value = "";
            }
        }
        /** handles keyboard navigation when the popup is focused */
        function onOptionKey(event, index) {
            if (event.key === "ArrowUp") {
                if (index < 1) {
                    inputField.value?.focus();
                }
                else {
                    getOptionWithId(index - 1)?.focus();
                }
            }
            else if (event.key === "ArrowDown") {
                getOptionWithId(index + 1)?.focus();
            }
            else if (event.key === "Escape") {
                close();
            }
        }
        /**
         * Closes popup when focus leaves this element
         * Because this component uses a Teleport, uses a custom `data-parent-id` attribute
         * to determine if the element is a child of this component
         */
        function onBlur(e) {
            const newTarget = e.relatedTarget;
            // close without refocusing open button
            if (!(newTarget instanceof HTMLElement)) {
                close(false);
            }
            else if (newTarget.getAttribute("data-parent-id") !== props.id) {
                close(false);
            }
        }
        /** emulates tab behavior, because options list is teleported to the app layer */
        function onCloseButtonTab(event) {
            if (!event.shiftKey) {
                getOptionWithId(0)?.focus();
                event.preventDefault();
            }
        }
        const closeButton = ref(null);
        /** finds and returns the next focusable element right after this component */
        function getNextFocusableElement() {
            if (!closeButton.value) {
                return;
            }
            const focusableElementSelector = "a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex='-1'])";
            const focusableElements = document.querySelectorAll(focusableElementSelector);
            const closeButtonIndex = Array.from(focusableElements).indexOf(closeButton.value);
            return focusableElements.item(closeButtonIndex + 1);
        }
        /**
         * Emulates continuous tab behavior.
         * This is required because part of the component is teleported to the app layer,
         * so this component is not continuous in the DOM
         */
        function onOptionTab(event, index) {
            if (index === 0 && event.shiftKey) {
                closeButton.value?.focus();
                event.preventDefault();
            }
            else if (index === trimmedOptions.value.length - 1) {
                getNextFocusableElement()?.focus();
                event.preventDefault();
            }
        }
        const root = ref(null);
        /** used to position the pop-up */
        const bounds = useElementBounding(root);
        watch(() => [props.options, props.selected], async () => {
            await nextTick();
            bounds.update();
        });
        whenever(isOpen, async () => {
            await nextTick();
            bounds.update();
        });
        return { __sfc: true, props, emit, inputField, openButton, isOpen, open, close, searchValue, trimmedSearchValue, optionsAsSet, searchValueEmpty, searchValueValid, filteredOptions, trimmedOptions, highlightedOption, onOptionHover, onInputUp, onInputDown, getOptionWithId, onOptionSelected, onInputEnter, onOptionKey, onBlur, onCloseButtonTab, closeButton, getNextFocusableElement, onOptionTab, root, bounds, FontAwesomeIcon, Vue2Teleport, normalizeTag };
    }
});
